"use client";

import { FormEvent, useLayoutEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { ArrowLeft, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EventTicketTotals } from "@/components/event-tickets/event-ticket-totals";
import {
  clampTicketQuantity,
  firstPartyTicketGuestLabel,
  scrollTicketPaymentStepIntoView,
  storeTicketCheckoutCapability,
  ticketPaymentTotalLabel,
  ticketPreCheckoutSummary,
  ticketBuyerCheckoutPayload,
  validateTicketBuyer,
  type CanonicalTicketTotals,
  type TicketBuyerDetails,
  type TicketBuyerErrors,
} from "@/components/event-tickets/event-ticket-public-controller";
import { parseLocalDate } from "@/lib/utils";
import { formatEventWhenLine, splitMenuHighlights } from "../event-detail-presentation";

const EventTicketPaymentForm = dynamic(
  () =>
    import("@/components/event-tickets/event-ticket-payment-form").then(
      (module) => module.EventTicketPaymentForm,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm text-muted-foreground">Loading secure payment…</span>
      </div>
    ),
  },
);

const preloadPaymentForm = () =>
  import("@/components/event-tickets/event-ticket-payment-form");

type Checkout = {
  clientSecret: string;
  purchaseId: string;
  accessToken: string;
} & CanonicalTicketTotals;

const initialBuyer: TicketBuyerDetails = {
  fullName: "",
  email: "",
  phone: "",
};

function isCheckout(value: unknown): value is Checkout {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<Checkout>;
  return (
    typeof candidate.clientSecret === "string" &&
    typeof candidate.purchaseId === "string" &&
    typeof candidate.accessToken === "string" &&
    typeof candidate.quantity === "number" &&
    typeof candidate.unitPriceCents === "number" &&
    typeof candidate.subtotalCents === "number" &&
    typeof candidate.taxCents === "number" &&
    typeof candidate.totalCents === "number" &&
    candidate.currency === "usd"
  );
}

export function TicketCheckoutClient({
  ticketEvent,
}: {
  ticketEvent: {
    id: string;
    slug: string;
    title: string;
    date: string;
    startTime: string;
    venueName: string;
    unitPriceCents: number;
    menuHighlight?: string;
    salesPaused?: boolean;
  };
}) {
  const [buyer, setBuyer] = useState<TicketBuyerDetails>(initialBuyer);
  const [buyerErrors, setBuyerErrors] = useState<TicketBuyerErrors>({});
  const [quantity, setQuantity] = useState(1);
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const paymentStepRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!checkout) return;
    const frame = window.requestAnimationFrame(() => {
      scrollTicketPaymentStepIntoView(paymentStepRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [checkout]);

  const updateBuyer = (key: keyof TicketBuyerDetails, value: string) => {
    setBuyer((current) => ({ ...current, [key]: value }));
    setBuyerErrors((current) => {
      if (!current[key]) return current;
      const remaining = { ...current };
      delete remaining[key];
      return remaining;
    });
  };

  const beginCheckout = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    setError(null);
    const validationErrors = validateTicketBuyer(buyer);
    if (Object.keys(validationErrors).length > 0) {
      setBuyerErrors(validationErrors);
      const firstInvalidField = Object.keys(validationErrors)[0];
      window.requestAnimationFrame(() => {
        formRef.current
          ?.querySelector<HTMLInputElement>(`#ticket-${firstInvalidField}`)
          ?.focus();
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/events/tickets/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          eventId: ticketEvent.id,
          buyer: ticketBuyerCheckoutPayload(buyer),
          quantity: clampTicketQuantity(quantity),
        }),
      });
      const result: unknown = await response.json().catch(() => null);
      if (!response.ok || !isCheckout(result)) {
        setError(
          response.status === 429
            ? "Too many checkout attempts. Please wait a few minutes and try again."
            : "We could not start ticket checkout. Please try again.",
        );
        return;
      }
      if (typeof window !== "undefined") {
        storeTicketCheckoutCapability(window, result.purchaseId, result.accessToken);
      }
      setCheckout({
        clientSecret: result.clientSecret,
        purchaseId: result.purchaseId,
        accessToken: result.accessToken,
        quantity: result.quantity,
        unitPriceCents: result.unitPriceCents,
        subtotalCents: result.subtotalCents,
        taxCents: result.taxCents,
        totalCents: result.totalCents,
        currency: result.currency,
      });
    } catch {
      setError("We could not start ticket checkout. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmationPath = checkout
    ? `/events/${encodeURIComponent(ticketEvent.slug)}/tickets/processing?purchase=${encodeURIComponent(checkout.purchaseId)}#ticket_token=${encodeURIComponent(checkout.accessToken)}`
    : "";
  const preCheckoutSummary = ticketPreCheckoutSummary({
    quantity,
    unitPriceCents: ticketEvent.unitPriceCents,
    currency: "usd",
  });
  const unitPriceLabel = ticketPreCheckoutSummary({
    quantity: 1,
    unitPriceCents: ticketEvent.unitPriceCents,
    currency: "usd",
  }).subtotalLabel;
  const whenLine = formatEventWhenLine({
    date: parseLocalDate(ticketEvent.date),
    startTime: ticketEvent.startTime,
  });
  const recapLine = `${ticketEvent.title} · ${whenLine} · ${unitPriceLabel}`;
  const pausedLabel = firstPartyTicketGuestLabel({
    ticketSalesPaused: ticketEvent.salesPaused,
    ticketPriceCents: ticketEvent.unitPriceCents,
  });
  const menuItems = ticketEvent.menuHighlight
    ? splitMenuHighlights(ticketEvent.menuHighlight)
    : [];

  const buyerField = ({
    key,
    label,
    autoComplete,
    name,
    type = "text",
    optional = false,
  }: {
    key: keyof TicketBuyerDetails;
    label: string;
    autoComplete: string;
    name: string;
    type?: "email" | "tel" | "text";
    optional?: boolean;
  }) => {
    const errorId = `ticket-${key}-error`;
    const errorMessage = buyerErrors[key];
    return (
      <div className="space-y-2">
        <Label htmlFor={`ticket-${key}`}>{label}</Label>
        <Input
          id={`ticket-${key}`}
          name={name}
          className="h-12"
          type={type}
          value={buyer[key]}
          onChange={(event) => updateBuyer(key, event.target.value)}
          autoComplete={autoComplete}
          inputMode={key === "phone" ? "tel" : key === "email" ? "email" : "text"}
          enterKeyHint={key === "phone" ? "done" : "next"}
          placeholder={
            key === "phone"
              ? "(702) 555-0123"
              : key === "fullName"
                ? "First and last name"
                : undefined
          }
          aria-invalid={Boolean(errorMessage)}
          aria-describedby={errorMessage ? errorId : undefined}
          aria-required={optional ? undefined : true}
        />
        {errorMessage ? (
          <p id={errorId} className="text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <main className="min-h-screen bg-background px-4 py-4 text-foreground sm:px-6 sm:py-6">
      <div className="mx-auto max-w-2xl">
        <Button asChild variant="outline" size="sm" className="rounded-full border-border bg-transparent hover:bg-muted">
          <Link href={`/events/${ticketEvent.slug}`}><ArrowLeft className="mr-2 h-4 w-4" /> Back to event</Link>
        </Button>
        <div className="mt-3 rounded-xl border border-border bg-card p-4 sm:mt-4 sm:p-6">
          {pausedLabel.paused ? (
            <div data-ticket-sales-paused="">
              <h1 className="font-serif text-xl leading-snug text-foreground sm:text-2xl">{recapLine}</h1>
              {menuItems.length > 0 ? (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm leading-5 text-foreground/80">
                  {menuItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              <Button
                type="button"
                className="mt-4 h-12 w-full rounded-full text-base font-semibold"
                disabled
              >
                {pausedLabel.label}
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">{ticketEvent.venueName}</p>
            </div>
          ) : !checkout ? (
            <form
              ref={formRef}
              className="space-y-3"
              autoComplete="on"
              noValidate
              onSubmit={beginCheckout}
            >
              <div>
                <h1 className="font-serif text-xl leading-snug text-foreground sm:text-2xl">{recapLine}</h1>
                {menuItems.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm leading-5 text-foreground/80">
                    {menuItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="mt-3 space-y-1.5">
                  <Label htmlFor="ticket-quantity">Admission passes</Label>
                  <div className="flex w-full max-w-52 items-center rounded-lg border border-input bg-background">
                    <Button type="button" variant="ghost" className="h-12 w-12 shrink-0 rounded-r-none" aria-label="Decrease admission passes" onClick={() => setQuantity((current) => clampTicketQuantity(current - 1))}><Minus className="h-4 w-4" /></Button>
                    <Input id="ticket-quantity" className="h-12 border-y-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" type="number" min={1} max={10} value={quantity} onChange={(event) => setQuantity(clampTicketQuantity(Number(event.target.value)))} inputMode="numeric" />
                    <Button type="button" variant="ghost" className="h-12 w-12 shrink-0 rounded-l-none" aria-label="Increase admission passes" onClick={() => setQuantity((current) => clampTicketQuantity(current + 1))}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-muted-foreground">{preCheckoutSummary.totalLabel}</p>
                </div>
              </div>
              {Object.keys(buyerErrors).length > 0 ? (
                <div role="alert" className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                  Check the highlighted fields, then continue to secure payment.
                </div>
              ) : null}
              <div className="space-y-3">
                {buyerField({
                  key: "fullName",
                  label: "Name",
                  autoComplete: "name",
                  name: "name",
                })}
                {buyerField({
                  key: "email",
                  label: "Email",
                  autoComplete: "email",
                  name: "email",
                  type: "email",
                })}
                {buyerField({
                  key: "phone",
                  label: "Phone (optional)",
                  autoComplete: "tel",
                  name: "tel",
                  type: "tel",
                  optional: true,
                })}
              </div>
              {error ? <p role="alert" className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">{error}</p> : null}
              <div data-event-primary-cta="">
                <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold" disabled={isSubmitting} onPointerEnter={preloadPaymentForm} onFocus={preloadPaymentForm}>
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing secure payment…</> : "Continue to payment"}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">One QR code checks in your full party together. Seats are held briefly once you start payment.</p>
              <p className="text-sm text-muted-foreground">{ticketEvent.venueName}</p>
              <p className="text-sm text-muted-foreground">Tax is calculated and confirmed on the next step.</p>
            </form>
          ) : (
            <div ref={paymentStepRef} className="scroll-mt-24">
              <h1 className="font-serif text-xl leading-snug text-foreground sm:text-2xl">{recapLine}</h1>
              <p className="mt-3 mb-4 text-sm text-muted-foreground">Your admission hold is reserved while you complete payment.</p>
              <EventTicketTotals totals={checkout} />
              <EventTicketPaymentForm
                clientSecret={checkout.clientSecret}
                confirmationPath={confirmationPath}
                totalLabel={ticketPaymentTotalLabel(checkout)}
                onPaymentSubmitted={() => window.location.assign(confirmationPath)}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
